"use client";

import { cn } from "@/lib/utils";

/** Presentational pieces `/admin/login` composes: the wordmark and the
 * spinner-free progress treatment. */

/**
 * The XGODO wordmark. v5 has no logo raster in `public/` — the brand mark is the
 * gradient "X" tile plus the wordmark, as the site header renders it — so this
 * draws it rather than importing the legacy PNG (which is a light-theme asset
 * and would sit badly on this dark canvas).
 *
 * `tone` picks the wordmark treatment: `solid` for flat dark surfaces, `gradient`
 * where the mark should carry the accent (what `/admin/login` uses).
 */
export function XgodoMark({
  size = "md",
  tone = "solid",
  className,
}: {
  size?: "sm" | "md" | "lg";
  tone?: "solid" | "gradient";
  className?: string;
}) {
  const tile = {
    sm: "size-7 rounded-md text-sm",
    md: "size-9 rounded-lg text-base",
    lg: "size-11 rounded-xl text-lg",
  }[size];
  const word = {
    sm: "text-base",
    md: "text-xl",
    lg: "text-2xl",
  }[size];

  return (
    <span className={cn("inline-flex items-center gap-2.5", className)}>
      <span
        aria-hidden="true"
        className={cn(
          "grid place-items-center bg-primary font-mono font-bold text-primary-foreground",
          tile,
        )}
      >
        X
      </span>
      <span
        className={cn(
          "font-semibold tracking-tight",
          word,
          // `gradient` used to clip a white→white/55 ramp into the wordmark.
          // Flattened in the 2026-08-03 Polymarket pass: Polymarket has no
          // gradient text, and white-alpha composites against the backdrop
          // instead of tracking the surface ramp. Kept as a distinct tone so
          // call sites don't change; both now resolve to solid foreground.
          tone === "gradient" ? "text-foreground" : "text-foreground",
        )}
      >
        XGODO
      </span>
    </span>
  );
}

/**
 * Indeterminate progress for the submitting state — a band sweeping along a
 * hairline track. Deliberately spinner-free (client ask): the page reads as
 * "working" without a rotating glyph. Renders nothing when idle, so it can be
 * left mounted.
 *
 * Static under `prefers-reduced-motion` (handled globally in globals.css).
 */
export function ProgressSweep({
  active,
  className,
  barClassName,
}: {
  active: boolean;
  className?: string;
  barClassName?: string;
}) {
  if (!active) return null;
  return (
    <div
      aria-hidden="true"
      className={cn("relative h-px w-full overflow-hidden bg-secondary", className)}
    >
      <div
        className={cn(
          "absolute inset-y-0 -left-1/3 w-1/3 animate-[shimmer_1.4s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-foreground/70 to-transparent",
          barClassName,
        )}
      />
    </div>
  );
}

/**
 * Screen-reader announcement for the submitting state, so the visual-only
 * progress treatment isn't the sole signal.
 */
export function SubmittingStatus({ active }: { active: boolean }) {
  return (
    <span role="status" aria-live="polite" className="sr-only">
      {active ? "Signing in" : ""}
    </span>
  );
}
